import React from 'react';
import { Trash2, Smartphone, Mail, AlertTriangle, Clock, HelpCircle, Database, Shield } from 'lucide-react';

const AccountDeletion: React.FC = () => {
  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-primary)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-left">
        {/* Header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Trash2 size={40} className="text-[#FFA800]" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-left">
              Suppression de Compte
            </h1>
          </div>
          <p className="text-[var(--color-text-secondary)] text-sm sm:text-base text-left">
            Comment demander la suppression de votre compte Ecopower et toutes vos données personnelles
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left">
            1. Comment supprimer votre compte
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4 text-left">
            Si vous souhaitez supprimer votre compte Ecopower et toutes les données associées, 
            vous pouvez le faire directement depuis l'application mobile ou en contactant notre support.
          </p>

          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-left flex items-center gap-2">
              <Smartphone size={20} className="text-[#FFA800]" />
              Depuis l'application mobile
            </h3>
            <ol className="list-decimal list-outside space-y-2 text-[var(--color-text-secondary)] ml-6 text-left">
              <li className="pl-2 text-left">Ouvrez l'application Ecopower sur votre appareil</li>
              <li className="pl-2 text-left">Allez dans <strong className="text-[var(--color-text-primary)]">Paramètres</strong> ou <strong className="text-[var(--color-text-primary)]">Profil</strong></li>
              <li className="pl-2 text-left">Sélectionnez <strong className="text-[var(--color-text-primary)]">Supprimer mon compte</strong></li>
              <li className="pl-2 text-left">Confirmez votre choix en entrant votre mot de passe</li>
              <li className="pl-2 text-left">Votre compte et toutes vos données seront supprimés définitivement</li>
            </ol>
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-left flex items-center gap-2">
              <Mail size={20} className="text-[#FFA800]" />
              Par email
            </h3>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-3 text-left">
              Si vous ne pouvez pas accéder à l'application, vous pouvez nous envoyer une demande 
              de suppression de compte par email à :
            </p>
            <p className="text-[var(--color-text-secondary)] mb-4 text-left">
              <strong className="text-[var(--color-text-primary)]">Email :</strong>{' '}
              <a 
                href="mailto:konouluc0@gmail.com" 
                className="text-[#FFA800] hover:underline transition-colors"
              >
                konouluc0@gmail.com
              </a>
            </p>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mb-3 text-left">
              Veuillez inclure dans votre email :
            </p>
            <ul className="list-disc list-outside space-y-2 text-[var(--color-text-secondary)] ml-6 text-left">
              <li className="pl-2 text-left">Votre adresse email associée au compte</li>
              <li className="pl-2 text-left">Votre nom complet</li>
              <li className="pl-2 text-left">La raison de la suppression (optionnel)</li>
            </ul>
            <p className="text-[var(--color-text-secondary)] leading-relaxed mt-4 text-left">
              Nous traiterons votre demande dans un délai de <strong className="text-[var(--color-text-primary)]">7 jours ouvrables</strong>.
            </p>
          </div>
        </section>

        {/* Data Deletion */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left flex items-center gap-2">
            <AlertTriangle size={24} className="text-[#FFA800]" />
            Données supprimées
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4 text-left">
            La suppression de votre compte entraînera la suppression permanente de :
          </p>
          <ul className="list-disc list-outside space-y-2 text-[var(--color-text-secondary)] ml-6 text-left">
            <li className="pl-2 text-left">Vos informations personnelles (nom, prénom, email, téléphone)</li>
            <li className="pl-2 text-left">Vos maisons et résidents associés</li>
            <li className="pl-2 text-left">Vos relevés de consommation</li>
            <li className="pl-2 text-left">Vos factures</li>
            <li className="pl-2 text-left">Vos messages et notifications</li>
            <li className="pl-2 text-left">Vos abonnements</li>
            <li className="pl-2 text-left">Toutes les autres données liées à votre compte</li>
          </ul>
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-yellow-800 dark:text-yellow-200 leading-relaxed text-left">
              <strong>Attention :</strong> Cette action est irréversible. Une fois votre compte supprimé, 
              vous ne pourrez plus récupérer vos données.
            </p>
          </div>
        </section>

        {/* Data Retention */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left flex items-center gap-2">
            <Database size={24} className="text-[#FFA800]" />
            Données conservées
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4 text-left">
            Conformément à la législation en vigueur, certaines données peuvent être conservées 
            pour des raisons légales ou comptables :
          </p>
          <ul className="list-disc list-outside space-y-2 text-[var(--color-text-secondary)] ml-6 text-left">
            <li className="pl-2 text-left"><strong className="text-[var(--color-text-primary)]">Données de facturation :</strong> Nécessaires pour la comptabilité (durée légale de conservation)</li>
            <li className="pl-2 text-left"><strong className="text-[var(--color-text-primary)]">Logs de sécurité et d'audit :</strong> Durée limitée pour des raisons de sécurité</li>
          </ul>
        </section>

        {/* Processing Time */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left flex items-center gap-2">
            <Clock size={24} className="text-[#FFA800]" />
            Délai de traitement
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4 text-left">
            Les demandes de suppression de compte sont traitées dans un délai maximum de 
            <strong className="text-[var(--color-text-primary)]"> 7 jours ouvrables</strong> à compter de la réception de votre demande.
          </p>
          <p className="text-[var(--color-text-secondary)] leading-relaxed text-left">
            Vous recevrez une confirmation par email une fois la suppression effectuée.
          </p>
        </section>

        {/* FAQ */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left flex items-center gap-2">
            <HelpCircle size={24} className="text-[#FFA800]" />
            Questions fréquentes
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-left">
              Puis-je récupérer mon compte après suppression ?
            </h3>
            <p className="text-[var(--color-text-secondary)] leading-relaxed text-left">
              Non, la suppression de compte est définitive et irréversible. Toutes vos données 
              sont supprimées de manière permanente.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-left">
              Que se passe-t-il si j'ai un abonnement actif ?
            </h3>
            <p className="text-[var(--color-text-secondary)] leading-relaxed text-left">
              Si vous avez un abonnement actif, celui-ci sera automatiquement annulé lors de 
              la suppression de votre compte. Aucun remboursement ne sera effectué pour la 
              période en cours.
            </p>
          </div>

          <div>
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-left">
              Puis-je supprimer seulement certaines données ?
            </h3>
            <p className="text-[var(--color-text-secondary)] leading-relaxed text-left">
              La suppression de compte est globale. Si vous souhaitez modifier ou supprimer 
              uniquement certaines données, vous pouvez le faire depuis l'application avant de 
              supprimer votre compte.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-[var(--color-border)]">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-left">
            Besoin d'aide ?
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed mb-6 text-left">
            Si vous avez des questions concernant la suppression de votre compte ou si vous 
            rencontrez des difficultés, n'hésitez pas à nous contacter :
          </p>
          <div className="space-y-3 text-[var(--color-text-secondary)]">
            <p className="text-left">
              <strong className="text-[var(--color-text-primary)]">Email :</strong>{' '}
              <a 
                href="mailto:konouluc0@gmail.com" 
                className="text-[#FFA800] hover:underline transition-colors"
              >
                konouluc0@gmail.com
              </a>
            </p>
            <p className="text-left">
              <strong className="text-[var(--color-text-primary)]">Téléphone :</strong> +228 97240460
            </p>
            <p className="text-left">
              <strong className="text-[var(--color-text-primary)]">Adresse :</strong> Lomé, Togo
            </p>
          </div>
        </section>

        {/* Legal Notice */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 bg-[var(--color-card-bg)] rounded-lg border border-[var(--color-border)]">
          <div className="flex items-start gap-4">
            <Shield size={24} className="text-[#FFA800] flex-shrink-0 mt-1" />
            <p className="text-[var(--color-text-secondary)] leading-relaxed text-left">
              La suppression de votre compte est une action définitive et irréversible. 
              Veuillez vous assurer de sauvegarder toutes les données importantes avant de procéder à la suppression.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletion;
